/**
 * Guided-walkthrough overlay.
 *
 * Renders a pulsing highlight ring + a bouncing "comic" arrow pointing at the
 * current step's target, plus an instruction label. The flow is deterministic
 * and LLM-free (see companion-walkthrough-store.ts): the model's only job was
 * deciding to launch it.
 *
 * Advance model: the user must click the REAL control (no "Next" shortcut).
 * Clicking step N's target performs the actual action (e.g. opening the
 * settings sheet), which is what makes step N+1's target exist. A "Next" button
 * would let the user skip ahead to a step whose target isn't mounted yet. The
 * one way out is a control that stays unfound for MISSING_AFTER_MS: the docked
 * label then says so and offers Skip step (never on the last step, where it
 * would count as finishing) and Stop.
 *
 * Positioning: a requestAnimationFrame loop re-resolves the target's rect every
 * frame, so the ring/arrow follow a target that mounts or SLIDES IN (the
 * settings sheet animates in from the right, a CSS transform that fires no
 * mutation/scroll events, so event-driven repositioning alone misses it). While
 * the target is off-screen or unlaid-out the ring hides and only the docked
 * instruction shows. The same loop times the missing-control message and
 * scrolls an off-screen control into view once its box has settled.
 *
 * The label is placed by floating-ui against a virtual reference built from
 * that per-frame rect, so label and ring can never disagree: below the
 * control, flipped above when it does not fit, shifted to stay on screen. The
 * arrow sits between the two on whichever side was picked.
 *
 * Mounted once in app-layout alongside the CompanionOverlay.
 */

import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { flip, offset, shift, useFloating, type VirtualElement } from '@floating-ui/react-dom'
import { useReducedMotion } from 'framer-motion'
import { EVENTS } from '@/services/analytics/events'
import { trackEvent } from '@/services/analytics/use-analytics'
import {
  MISSING_AFTER_MS,
  SCROLL_SETTLE_MS,
  panelWindowOf,
  targetSelector,
  useWalkthroughStore,
  type WalkthroughStep,
} from './companion-walkthrough-store'
import { textPx } from '@/features/theme-editor/text-scale'

interface Rect {
  left: number
  top: number
  width: number
  height: number
  /** The control's own corner radius, so the highlight follows its shape. */
  radius: number
}

/** One step of one launch (the store copies `steps` on every start). */
interface StepKey {
  steps: WalkthroughStep[]
  index: number
}

/** Padding between the control's edge and the highlight outline. */
const RING_PAD = 4
/** Controls up to this size (and roughly square) are icons and get a circle. */
const ICON_MAX = 44

const LABEL_W = 250
const ARROW_W = 22
const ARROW_H = 46
/** Space between the control and the arrow, and between the arrow and the label. */
const ARROW_GAP = 4

/**
 * Below the control by default, flipped above when the label does not fit,
 * shifted sideways to stay 12 px inside the viewport. The offset leaves room
 * for the arrow between control and label.
 */
const LABEL_MIDDLEWARE = [
  offset(ARROW_H + ARROW_GAP * 2),
  flip({ fallbackPlacements: ['top'], padding: 12 }),
  shift({ padding: 12 }),
]

/** With no visible target the label docks at the bottom centre. */
const DOCKED: CSSProperties = {
  position: 'fixed',
  left: '50%',
  bottom: 24,
  transform: 'translateX(-50%)',
}

/** The small text buttons under the missing-control line. */
const TEXT_BUTTON: CSSProperties = {
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: 'hsl(var(--primary))',
  cursor: 'pointer',
  fontSize: textPx(11),
  fontWeight: 600,
}

/**
 * Resolve a step target to a live element, anchored to the walkthrough's panel.
 * Scoped to the anchored panel's window first (the gear is in the mosaic
 * toolbar, not the [data-panel-id] body; see panelWindowOf); falls back to a
 * global lookup for targets in a portaled sheet (only one sheet is open).
 */
function resolveElement(target: string, panelId: string | null): HTMLElement | null {
  const sel = targetSelector(target)
  if (panelId) {
    const scoped = panelWindowOf(panelId)?.querySelector<HTMLElement>(sel)
    if (scoped) return scoped
  }
  return document.querySelector<HTMLElement>(sel)
}

function sameRect(a: Rect | null, b: Rect | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.left === b.left &&
    a.top === b.top &&
    a.width === b.width &&
    a.height === b.height &&
    a.radius === b.radius
  )
}

function sameBox(a: DOMRect, b: DOMRect): boolean {
  return a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height
}

function sameStep(a: StepKey | null, b: StepKey): boolean {
  return a !== null && a.steps === b.steps && a.index === b.index
}

/** Within the viewport, at least in part (not mid slide-in off-screen). */
function onScreen(r: DOMRect): boolean {
  return !(
    r.left >= window.innerWidth ||
    r.right <= 0 ||
    r.top >= window.innerHeight ||
    r.bottom <= 0
  )
}

function toRect(el: HTMLElement, r: DOMRect): Rect {
  // The control's real corner radius (first corner is enough; controls are
  // uniform). jsdom and unstyled elements report '' → 0.
  const radius = parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0
  return { left: r.left, top: r.top, width: r.width, height: r.height, radius }
}

/** floating-ui reference for the control's box as the rAF loop last saw it. */
function virtualReference(r: Rect): VirtualElement {
  const box = {
    x: r.left,
    y: r.top,
    left: r.left,
    top: r.top,
    width: r.width,
    height: r.height,
    right: r.left + r.width,
    bottom: r.top + r.height,
  }
  return { getBoundingClientRect: () => box }
}

/** Deterministic pseudo-random offset in [-1.5, 1.5]: the "roughness" of a hand-drawn stroke. */
function wobble(seed: number, i: number): number {
  const x = Math.sin(seed * 9301 + i * 49297) * 233280
  return (x - Math.floor(x)) * 3 - 1.5
}

/**
 * One stroke of a hand-drawn line: a quadratic curve whose midpoint and end are
 * nudged by a seeded wobble, the way rough.js draws Excalidraw's arrows.
 */
function roughSegment(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  seed: number,
  i: number,
): string {
  const mx = (x1 + x2) / 2 + wobble(seed, i)
  const my = (y1 + y2) / 2 + wobble(seed, i + 1)
  const ex = x2 + wobble(seed, i + 2) * 0.5
  const ey = y2 + wobble(seed, i + 3) * 0.5
  const f = (n: number) => Math.round(n * 10) / 10
  return `M${f(x1)} ${f(y1)} Q${f(mx)} ${f(my)} ${f(ex)} ${f(ey)}`
}

export function CompanionWalkthrough() {
  const active = useWalkthroughStore((s) => s.active)
  const steps = useWalkthroughStore((s) => s.steps)
  const index = useWalkthroughStore((s) => s.index)
  const scope = useWalkthroughStore((s) => s.scope)
  const advance = useWalkthroughStore((s) => s.advance)
  const dismiss = useWalkthroughStore((s) => s.dismiss)
  const reduced = useReducedMotion() === true

  const step = active ? steps[index] : undefined
  const target = step?.target
  const panelId = scope?.panelId ?? null

  const [rect, setRect] = useState<Rect | null>(null)
  /** The step whose control has gone unfound for MISSING_AFTER_MS. */
  const [missingFor, setMissingFor] = useState<StepKey | null>(null)
  /** Steps already reported missing / already scrolled to: once per step each. */
  const reportedRef = useRef<StepKey | null>(null)
  const scrolledRef = useRef<StepKey | null>(null)

  // ── Follow the target every frame (mount + slide-in animations) ────────────
  // Also times the missing-control message and the scroll-into-view settle, so
  // one loop owns everything that depends on where the target is.
  useEffect(() => {
    if (!target) {
      setRect(null)
      return
    }
    const key: StepKey = { steps, index }
    let raf = 0
    // Missing control: unfound since (null = found), whether this loop raised
    // the message, and whether a found control must clear one (an earlier run
    // of this loop for the same step may have left it set).
    let hiddenSince: number | null = null
    let raised = false
    let clearOnFind = true
    // Scroll settle: the off-screen box last frame and since when it held still.
    let lastBox: DOMRect | null = null
    let stillSince = 0

    const reportMissing = () => {
      if (sameStep(reportedRef.current, key)) return
      reportedRef.current = key
      const { meta } = useWalkthroughStore.getState()
      if (!meta) return
      trackEvent(EVENTS.WALKTHROUGH_TARGET_MISSING, {
        slug: meta.slug,
        source: meta.source,
        step: index + 1,
        steps: steps.length,
        target,
      })
    }

    const tick = () => {
      const now = performance.now()
      const el = resolveElement(target, panelId)
      const box = el?.getBoundingClientRect()
      const laidOut = !!el && !!box && box.width > 0 && box.height > 0
      const next = laidOut && onScreen(box) ? toRect(el, box) : null
      setRect((prev) => (sameRect(prev, next) ? prev : next))

      if (next) {
        hiddenSince = null
        raised = false
        if (clearOnFind) {
          clearOnFind = false
          setMissingFor(null)
        }
      } else {
        hiddenSince ??= now
        if (!raised && now - hiddenSince >= MISSING_AFTER_MS) {
          raised = true
          clearOnFind = true
          setMissingFor(key)
          reportMissing()
        }
      }

      // Laid out but off-screen: scroll it in once its box holds still. A box
      // still moving is mid-animation, and scrolling to it would yank the page.
      if (laidOut && !next && !sameStep(scrolledRef.current, key)) {
        if (!lastBox || !sameBox(lastBox, box)) {
          lastBox = box
          stillSince = now
        } else if (now - stillSince >= SCROLL_SETTLE_MS) {
          scrolledRef.current = key
          el.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
            behavior: reduced ? 'auto' : 'smooth',
          })
        }
      } else {
        lastBox = null
      }

      raf = requestAnimationFrame(tick)
    }
    tick()
    return () => cancelAnimationFrame(raf)
  }, [target, panelId, steps, index, reduced])

  // ── Place the label against the ring's rect ────────────────────────────────
  const reference = useMemo(() => (rect ? virtualReference(rect) : null), [rect])
  // `open` follows the rect so `isPositioned` resets whenever the control goes
  // away: until floating-ui has placed the label, it stays docked instead of
  // flashing at the corner (floatingStyles start at 0,0).
  const {
    refs: { setFloating },
    floatingStyles,
    placement,
    update,
    isPositioned,
  } = useFloating({
    strategy: 'fixed',
    placement: 'bottom',
    middleware: LABEL_MIDDLEWARE,
    transform: false,
    open: rect !== null,
    elements: { reference },
  })
  // A new rect re-places the label on its own (new reference). A new step can
  // keep the rect but change the label's height (its text), so re-place on
  // every step too.
  useLayoutEffect(() => {
    update()
  }, [update, step])

  // ── Advance when the user clicks the current (anchored) target ─────────────
  useEffect(() => {
    if (!active || !target) return
    const onClick = (e: MouseEvent) => {
      const sel = targetSelector(target)
      const hit = (e.target as Element | null)?.closest?.(sel)
      if (!hit) return
      // Panel-local targets (the gear) must be THE anchored panel's instance so
      // a different panel's gear can't drive the flow. Portaled targets (the
      // settings-sheet tabs and the per-row Add buttons, not inside the panel
      // window) advance on ANY match, since only one sheet is open: clicking any
      // column's Add completes the step (don't force the one we happen to ring).
      const scopeEl = panelId ? panelWindowOf(panelId) : null
      const scopedTarget = scopeEl?.querySelector(sel)
      if (!scopedTarget || hit === scopedTarget) {
        requestAnimationFrame(() => advance())
      }
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [active, target, panelId, advance])

  // ── Esc dismisses ──────────────────────────────────────────────────────────
  const handleEsc = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss()
    },
    [dismiss],
  )
  useEffect(() => {
    if (!active) return
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [active, handleEsc])

  if (!active || !step) return null

  const total = steps.length
  const stepNo = index + 1
  const isLast = stepNo >= total
  const missing = !rect && missingFor?.steps === steps && missingFor.index === index

  const center = rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : null
  // The highlight follows the control's shape: its box plus a little
  // breathing room. Small icon controls (roughly square, up to ICON_MAX) get
  // a circle; every other control keeps its own corners, so a wide button gets
  // a rounded rectangle or a pill, never a huge circle around it.
  const ringW = rect ? rect.width + RING_PAD * 2 : 0
  const ringH = rect ? rect.height + RING_PAD * 2 : 0
  const iconLike = rect
    ? Math.abs(rect.width - rect.height) <= 6 && Math.max(rect.width, rect.height) <= ICON_MAX
    : false
  const ringRadius = !rect
    ? 0
    : iconLike
      ? 9999
      : Math.min(rect.radius + RING_PAD, Math.min(ringW, ringH) / 2)

  // The arrow sits between the control and the label, on the side floating-ui
  // picked for the label, at the control's centre (not the shifted label's).
  const labelAbove = placement.startsWith('top')
  const arrowTop = rect
    ? labelAbove
      ? rect.top - ARROW_GAP - ARROW_H
      : rect.top + rect.height + ARROW_GAP
    : 0

  return (
    <div
      data-testid="companion-walkthrough"
      style={{ position: 'fixed', inset: 0, zIndex: 10002, pointerEvents: 'none' }}
    >
      <style>{`
        @keyframes wt-pulse {
          0%   { transform: translate(-50%,-50%) scale(1);    box-shadow: 0 0 0 0 hsl(var(--primary) / 0.45); }
          70%  { transform: translate(-50%,-50%) scale(1.06); box-shadow: 0 0 0 6px hsl(var(--primary) / 0); }
          100% { transform: translate(-50%,-50%) scale(1);    box-shadow: 0 0 0 0 hsl(var(--primary) / 0); }
        }
        @keyframes wt-bounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
      `}</style>

      {/* Pulsing highlight ring on the target (still under reduced motion) */}
      {rect && center && (
        <div
          data-testid="walkthrough-ring"
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: center.x,
            top: center.y,
            width: ringW,
            height: ringH,
            borderRadius: ringRadius,
            border: '2px solid hsl(var(--primary))',
            transform: 'translate(-50%,-50%)',
            animation: reduced ? undefined : 'wt-pulse 1.4s ease-out infinite',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Bouncing arrow pointing at the target (still under reduced motion) */}
      {rect && center && (
        <div
          data-testid="walkthrough-arrow"
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: center.x - ARROW_W / 2,
            top: arrowTop,
            transform: labelAbove ? 'rotate(180deg)' : 'none',
            pointerEvents: 'none',
          }}
        >
          <div style={{ animation: reduced ? undefined : 'wt-bounce 1.1s ease-in-out infinite' }}>
            {/* Hand-drawn arrow in the Excalidraw style: each stroke is drawn
                twice with a small deterministic wobble (seeded by the step so
                it does not jitter between frames), round caps, open head.
                Points UP; parent rotates 180° when the label sits above. */}
            <svg width={ARROW_W} height={ARROW_H} viewBox="0 0 22 46" fill="none" aria-hidden>
              {[0, 1].map((pass) => (
                <g
                  key={pass}
                  stroke="hsl(var(--primary))"
                  strokeWidth={pass === 0 ? 2 : 1.4}
                  strokeOpacity={pass === 0 ? 1 : 0.55}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d={roughSegment(11, 43, 11, 12, stepNo + pass * 7, 0)} />
                  <path d={roughSegment(11, 6, 4, 15, stepNo + pass * 7, 4)} />
                  <path d={roughSegment(11, 6, 18, 15, stepNo + pass * 7, 8)} />
                </g>
              ))}
            </svg>
          </div>
        </div>
      )}

      {/* What a screen reader announces: the step, its instruction and the
          missing-control line, without the buttons' names. One live region for
          the whole walkthrough (no per-step key), so each new instruction is
          announced in place. */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {`Step ${stepNo} of ${total}: ${step.instruction}`}
        {missing ? " I can't find that control here." : ''}
      </div>

      {/* Instruction label */}
      <div
        ref={setFloating}
        data-testid="walkthrough-label"
        style={{
          ...(rect && isPositioned ? floatingStyles : DOCKED),
          width: LABEL_W,
          pointerEvents: 'auto',
          background: 'hsl(var(--popover))',
          color: 'hsl(var(--popover-foreground))',
          border: '1px solid hsl(var(--primary) / 0.44)',
          borderRadius: 10,
          padding: '10px 12px',
          boxShadow: '0 10px 30px hsl(0 0% 0% / 0.75)',
          fontSize: textPx(13),
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: textPx(11), fontWeight: 600, color: 'hsl(var(--primary))' }}>
            Step {stepNo} of {total}
          </span>
          {/* Small pill, like the app's ghost buttons: a thin border, a compact
              glyph, a hover fill. The dismiss must not read as a primary action. */}
          <button
            type="button"
            aria-label="Dismiss walkthrough"
            title="Dismiss (Esc)"
            onClick={dismiss}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'hsl(var(--hover))')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 20,
              minWidth: 28,
              padding: '0 8px',
              border: '1px solid hsl(var(--border-strong))',
              borderRadius: 9999,
              background: 'transparent',
              color: 'hsl(var(--muted-foreground))',
              cursor: 'pointer',
              fontSize: textPx(10),
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        <p style={{ margin: '5px 0 0', lineHeight: 1.35 }}>
          {step.instruction}
          {/* Waiting for the control. Hidden from the live region so the
              control coming and going does not re-announce the step. */}
          {!rect && <span aria-hidden="true"> …</span>}
        </p>
        {missing && (
          <>
            <p
              style={{ margin: '6px 0 0', lineHeight: 1.35, color: 'hsl(var(--muted-foreground))' }}
            >
              {"I can't find that control here."}
            </p>
            <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
              {/* Skipping the last step would count as finishing the walkthrough. */}
              {!isLast && (
                <button type="button" onClick={advance} style={TEXT_BUTTON}>
                  Skip step
                </button>
              )}
              <button type="button" onClick={dismiss} style={TEXT_BUTTON}>
                Stop
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
